import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const Bruxism = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Bruxism & Teeth Grinding Treatment Fort Worth TX | Fort Worth Dental</title>
        <meta name="description" content="Bruxism and teeth grinding treatment in Fort Worth TX. Custom night guards, TMJ therapy. Stop damaging your teeth from grinding. Call (817) 985-3337." />
        <meta name="keywords" content="bruxism fort worth, teeth grinding treatment fort worth tx, night guard, TMJ treatment, jaw clenching, tooth grinding" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Bruxism Treatment in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Stop Teeth Grinding and Jaw Clenching</p>
            <p className="text-sm mt-4 opacity-90">Teeth Grinding Treatment | TMJ Relief | Night Guard Therapy</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Teeth Grinding */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/bruxism-new-1.jpg" 
                  alt="Teeth grinding bruxism treatment Fort Worth TX - Patient suffering from jaw pain and tooth wear due to nocturnal teeth clenching" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Teeth Grinding?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Teeth grinding, technically called bruxism, occurs when a patient forcefully clenches his teeth together during sleep. Sometimes this is audible and makes a grating sound. Other times it is more subtle and silent. Either situation carries the potential for problems. While many people do this on an occasional basis with no damage done, some patients develop a habit of grinding until there is damage to the teeth or jaw. This can develop into other health complications over time. A big part of the problem is that people are often unaware that they have this problem because it usually happens during their sleep. It does, however, occasionally happen while the patient is awake.
                </p>
              </div>
            </div>
          </section>

          {/* What Problems Can Bruxism Produce */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Problems Can Bruxism Produce?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  While it is possible that teeth grinding may produce no problems for some patients, it can also become serious for others. A habitual problem with teeth grinding can eventually produce some serious dental complications. These complications can include loose teeth, fractured teeth, or even the eventual loss of teeth. If this situation is not dealt with, teeth grinding can wear down teeth until dental procedures are required. The procedures can include dental implants, crowns, bridges, root canals, or dentures. In some cases, teeth grinding can damage the jawbone. Teeth grinding has even been known to bring about a loss of hearing, cosmetic changes in the appearance of the patient's face, and difficulties for the temporomandibular joint (the joining of jaw to skull).
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/bruxism-new-2.jpg" 
                  alt="Bruxism complications Fort Worth dentist - Damaged teeth and jaw problems from chronic teeth grinding requiring dental treatment" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Why Do I Grind My Teeth */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/bruxism-new-1.jpg" 
                  alt="Causes of teeth grinding during sleep - Stress and anxiety related bruxism diagnosis at Fort Worth Dental" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Why Do I Grind My Teeth While I Sleep?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  There are several possibilities when it comes to contributing factors for bruxism. Children may grind their teeth do to an infection or a cold. Sometimes teeth grinding can be attributed to the pain that comes with teething or an earache. For adults, stress and anxiety are often contributing factors. In some cases, an abnormality in bite or teeth alignment can be a factor.
                </p>
              </div>
            </div>
          </section>

          {/* How Can I Stop Grinding */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  How Can I Stop Grinding My Teeth?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  While it is important to diagnose and address any root problems such as stress, earaches, or misaligned teeth, it is also important to stop the grinding while these problems are being addressed. A common solution is the use of a night guard which protects your teeth while you sleep. Your dentist can custom fit a mouth guard for you.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/bruxism-new-2.jpg" 
                  alt="Custom night guard for teeth grinding Fort Worth TX - Protective dental appliance to prevent bruxism damage while sleeping" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Bruxism Treatment?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team provides custom night guards and comprehensive bruxism treatment to protect your teeth.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Custom Night Guards</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Bruxism Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about bruxism treatment in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Bruxism Treatment Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Bruxism;